import type { UserType } from '../services/api/models/UserType';

/** Display labels for agency-scoped clinical titles (EMR-699). */
const USER_TYPE_LABELS: Record<UserType, string> = {
  CHAPLAIN: 'Chaplain',
  COTA: 'COTA',
  HHA: 'HHA',
  LPN_LVN: 'LPN/LVN',
  MSW: 'MSW',
  OT: 'OT',
  OTHER: 'Other',
  PT: 'PT',
  PTA: 'PTA',
  RN: 'RN',
  ST: 'ST',
};

export const USER_TYPE_OPTIONS: Array<{ value: UserType; label: string }> = (
  Object.keys(USER_TYPE_LABELS) as UserType[]
).map(value => ({
  value,
  label: USER_TYPE_LABELS[value],
}));

export function userTypeLabel(userType: UserType | null | undefined): string {
  if (!userType) return '';
  return USER_TYPE_LABELS[userType];
}

/** Collapse a credential token to letters/digits only, upper-cased, for dedup comparisons. */
function normalizeCredentialToken(token: string): string {
  return token.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/**
 * Split a credentials/label string on ',' and '/' into normalized comparison
 * tokens. Splitting on '/' too lets a combined title like "LPN/LVN" match
 * credentials listed either as one token ("LPN/LVN") or two ("LPN, LVN").
 */
function credentialTokens(value: string): Set<string> {
  const tokens = new Set<string>();
  for (const commaPart of value.split(',')) {
    for (const slashPart of commaPart.split('/')) {
      const normalized = normalizeCredentialToken(slashPart);
      if (normalized) tokens.add(normalized);
    }
  }
  return tokens;
}

export function appendUserTypeToDisplayName(
  name: string,
  userType: UserType | null | undefined,
  credentials?: string | null
): string {
  const trimmed = name.trim();
  const suffixes: string[] = [];
  const cred = credentials?.trim();
  const typeLabel = userTypeLabel(userType);
  const credTokens = credentialTokens(cred ?? '');
  if (cred) suffixes.push(cred);
  if (typeLabel) {
    const labelTokens = credentialTokens(typeLabel);
    const isFullyCovered = [...labelTokens].every(token =>
      credTokens.has(token)
    );
    if (!isFullyCovered) suffixes.push(typeLabel);
  }
  if (!trimmed) return suffixes.join(', ');
  if (suffixes.length === 0) return trimmed;
  return `${trimmed}, ${suffixes.join(', ')}`;
}
